import type { EvidenceSource, LocalDate } from '@cadencia/contracts';
import { esClaimDeLargoPlazo, isEvidenceClaim } from './claims.js';

/**
 * Qué ficha es válida, y cuál puede pasar de candidata a utilizable (§8).
 *
 * EL §8 SEPARA CUATRO COSAS Y ESTE FICHERO CUBRE LAS DOS PRIMERAS:
 *
 *   1. búsqueda y síntesis  → produce fichas `candidate`
 *   2. **evaluación y activación** → `promoteSource` decide si pasan a `active`
 *   3. uso en una programación individual → `citations.ts` y el contexto de IA
 *   4. cambios de software → los hace una persona, con revisión y pruebas
 *
 * La frontera entre 1 y 2 es la que impide que lo que el modelo devuelva entre
 * solo en las decisiones. Una ficha recién recuperada NO la usa nadie.
 *
 * NADA DE ESTO JUZGA LA CIENCIA. Esto comprueba que la ficha está completa, que
 * es localizable y que no se le pide sostener algo que su diseño no sostiene.
 * Que un estudio sea bueno no lo decide una función: lo declara la ficha en
 * `certainty`, y de dónde sale eso está en `limitations` y en la búsqueda que
 * la trajo.
 */

export interface SourceVerdict {
  ok: boolean;
  /** Vocabulario del dominio, no prosa para el usuario. */
  violations: string[];
}

/**
 * Una ficha bien formada.
 *
 * Lo que se comprueba, y por qué cada cosa:
 *
 * * **Identificador verificable.** Al menos DOI, PMID o URL. El §8 lo pide por
 *   su nombre, y sin ninguno la ficha es una afirmación con formato de cita:
 *   nadie puede ir al documento a contradecirla.
 * * **Afirmaciones del vocabulario.** Una `claim` inventada no la lee ningún
 *   motor, así que la ficha entraría en la base sin poder respaldar nada y
 *   pareciendo que respalda algo.
 * * **Una fuente aguda no respalda una afirmación de largo plazo.** La regla
 *   dura del §8, y la que de verdad protege: es el error más fácil de cometer
 *   y el más difícil de ver, porque la ficha puede ser impecable.
 * * **Una prepublicación no puede declararse revisada por pares.** Son dos
 *   campos independientes y esta es la única combinación imposible.
 * * **Certeza alta exige texto completo.** No se puede afirmar certeza alta
 *   sobre lo que solo se leyó en un resumen: el resumen cuenta lo que sus
 *   autores quisieron destacar, y las limitaciones que calificarían esa certeza
 *   viven en el cuerpo del artículo.
 */
export function validateEvidenceSource(source: EvidenceSource): SourceVerdict {
  const violations: string[] = [];

  if (source.doi === null && source.pmid === null && source.url === null) {
    violations.push('sin identificador verificable: hace falta DOI, PMID o enlace');
  }

  for (const claim of source.claims) {
    if (!isEvidenceClaim(claim)) violations.push(`afirmación desconocida: ${claim}`);
  }

  if (source.outcomeHorizon === 'acute') {
    for (const claim of source.claims) {
      if (esClaimDeLargoPlazo(claim)) {
        violations.push(`una fuente aguda no respalda «${claim}», que es de largo plazo`);
      }
    }
  }

  if (source.sourceType === 'preprint' && source.peerReviewed) {
    violations.push('una prepublicación no está revisada por pares');
  }

  if (source.certainty === 'high' && !source.fullTextAccess) {
    violations.push('certeza alta sin haber leído el texto completo');
  }

  return { ok: violations.length === 0, violations };
}

// ---------------------------------------------------------------------------
// Promoción: de candidata a utilizable
// ---------------------------------------------------------------------------

/**
 * Requisitos MÍNIMOS para que una ficha entre en las decisiones.
 *
 * Son deliberadamente pocos y deliberadamente duros. El §8 pide «criterios de
 * promoción de una ficha candidata a evidencia utilizable», no un algoritmo de
 * puntuación: un número compuesto a partir de cinco ejes se convierte en algo
 * que se puede maquillar subiendo el que más fácil sea.
 */
export const PROMOTION_RULES = {
  /**
   * Una heurística NUNCA se promueve por esta vía.
   *
   * No porque valga menos, sino porque no es evidencia: promoverla la metería
   * en la base como si un documento la respaldara. Las heurísticas del motor
   * viven en el código, declaradas como convención —`PHASE_RAMPS` dice de sí
   * mismo que lo es— y ahí es donde se discuten.
   */
  soloEvidencia: true,
  /** Sin revisión por pares no se activa: como mucho, se conserva candidata. */
  exigeRevisionPorPares: true,
  /**
   * Aplicabilidad `speculative` no entra.
   *
   * Es el eje que separa «esto está bien medido» de «esto se parece a mi
   * caso». Una extrapolación desde otra población puede informar una
   * conversación; no puede mover una prescripción sin que alguien lo mire.
   */
  aplicabilidadMinima: ['direct', 'indirect'] as const,
  /**
   * Certeza `very_low` tampoco.
   *
   * Se queda candidata y sigue siendo visible: el §8 no pide tirar lo débil,
   * pide no usarlo como si fuera fuerte.
   */
  certezaMinima: ['high', 'moderate', 'low'] as const,
} as const;

export interface PromotionVerdict {
  /** Si puede pasar a `active`. */
  promote: boolean;
  /** Por qué no, en vocabulario del dominio. Vacío si se promueve. */
  blockers: string[];
}

/**
 * ¿Puede esta ficha pasar a utilizarse en una programación?
 *
 * **La promoción no es automática aunque esto diga que sí.** Devuelve si la
 * ficha CUMPLE los requisitos; quién aprieta el botón es una persona, y el §8
 * es explícito en que «la IA en producción no puede reescribir su propio motor»
 * ni activarse conocimiento a sí misma. Registrar cómo se verificó forma parte
 * del paso, y por eso `statusNote` es donde se escribe.
 */
export function canPromote(source: EvidenceSource): PromotionVerdict {
  const blockers: string[] = [];

  const forma = validateEvidenceSource(source);
  if (!forma.ok) blockers.push(...forma.violations);

  if (source.status === 'retracted') blockers.push('la fuente está retractada');
  if (source.status === 'superseded') blockers.push('la fuente está superada por otra');

  if (PROMOTION_RULES.soloEvidencia && source.kind !== 'evidence') {
    blockers.push('una heurística no se promueve como evidencia');
  }
  if (PROMOTION_RULES.exigeRevisionPorPares && !source.peerReviewed) {
    blockers.push('sin revisión por pares');
  }
  if (!(PROMOTION_RULES.aplicabilidadMinima as readonly string[]).includes(source.applicability)) {
    blockers.push(`aplicabilidad ${source.applicability}: demasiado lejos de este atleta`);
  }
  if (!(PROMOTION_RULES.certezaMinima as readonly string[]).includes(source.certainty)) {
    blockers.push(`certeza ${source.certainty}`);
  }

  return { promote: blockers.length === 0, blockers };
}

// ---------------------------------------------------------------------------
// Qué se puede usar hoy
// ---------------------------------------------------------------------------

/**
 * Las fichas que una programación puede citar: activas y no retractadas.
 *
 * `superseded` tampoco entra. No es que sea falsa —puede seguir siendo
 * correcta— es que hay otra que la reemplaza, y citar las dos para la misma
 * afirmación cuenta el mismo hallazgo dos veces.
 */
export function usableSources(sources: readonly EvidenceSource[]): EvidenceSource[] {
  return sources.filter((fila) => fila.status === 'active' && fila.deletedAt === null);
}

/**
 * Lo que respalda una afirmación concreta, de lo utilizable.
 *
 * Ordenado por lo que de verdad importa al aplicarlo a UNA persona: primero lo
 * directamente aplicable, y a igualdad lo de mayor certeza. No hay puntuación
 * compuesta: son dos criterios y se aplican en orden, que es reproducible y se
 * puede discutir.
 */
const ORDEN_APLICABILIDAD: Record<string, number> = { direct: 0, indirect: 1, speculative: 2 };
const ORDEN_CERTEZA: Record<string, number> = { high: 0, moderate: 1, low: 2, very_low: 3 };

/** Lo utilizable, de más a menos aplicable y, a igualdad, de más a menos cierto. */
export function rankedUsableSources(sources: readonly EvidenceSource[]): EvidenceSource[] {
  return usableSources(sources).sort((a, b) => {
    const aplic =
      (ORDEN_APLICABILIDAD[a.applicability] ?? 9) - (ORDEN_APLICABILIDAD[b.applicability] ?? 9);
    if (aplic !== 0) return aplic;
    return (ORDEN_CERTEZA[a.certainty] ?? 9) - (ORDEN_CERTEZA[b.certainty] ?? 9);
  });
}

export function sourcesForClaim(
  sources: readonly EvidenceSource[],
  claim: string,
): EvidenceSource[] {
  return usableSources(sources)
    .filter((fila) => fila.claims.includes(claim))
    .sort((a, b) => {
      const aplic =
        (ORDEN_APLICABILIDAD[a.applicability] ?? 9) - (ORDEN_APLICABILIDAD[b.applicability] ?? 9);
      if (aplic !== 0) return aplic;
      return (ORDEN_CERTEZA[a.certainty] ?? 9) - (ORDEN_CERTEZA[b.certainty] ?? 9);
    });
}

// ---------------------------------------------------------------------------
// Retractaciones
// ---------------------------------------------------------------------------

/**
 * Retira una fuente sin borrarla, y dice por qué.
 *
 * El §8 pide «gestionar fuentes corregidas, retiradas o retractadas,
 * conservando trazabilidad de los planes que las utilizaron». Por eso esto
 * cambia el estado en vez de eliminar la fila: un bloque de hace tres meses
 * citó lo que citó, y reescribir el pasado para que parezca que nunca se apoyó
 * en algo retractado es peor que el error original.
 *
 * Sube `version` porque la ficha cambia. Quien mire un plan viejo verá la
 * versión que se usó y, al lado, que hoy está retractada.
 */
export function retractSource(source: EvidenceSource, motivo: string, hoy: LocalDate): EvidenceSource {
  return {
    ...source,
    status: 'retracted',
    statusNote: motivo,
    reviewedOn: hoy,
    version: Math.min(999, source.version + 1),
  };
}
